import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FieldArray } from 'redux-form';
import inflection from 'inflection';

import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import COLORS from '../../../../theme/colors';
//import TextFieldLabel from 'material-ui/TextField/TextFieldLabel';
//import ContentAdd from 'material-ui/svg-icons/content/add';
//import ActionDeleteIcon from 'material-ui/svg-icons/action/delete';

import { translate } from 'react-admin';

import EmbeddedArrayInputFormField from '../form/EmbeddedArrayInputFormField';

/**
 * An Input component for generating/editing an embedded array
 *
 *
 * Use it with any set of input components as children, like `<TextInput>`,
 * `<SelectInput>`, `<RadioButtonGroupInput>`, etc.
 *
 * You must define the targeted field for each child or only use one child for primitive arrays.
 *
 * @example
 * export const CommentEdit = (props) => (
 *     <Edit {...props}>
 *         <SimpleForm>
 *              <EmbeddedArrayInput source="links">
 *                  <TextInput source="url" />
 *                  <TextInput source="context"/>
 *                  <ReferenceInput resource="tags" reference="tags" source="tag_id" >
 *                      <SelectInput optionText="name" />
 *                  </ReferenceInput>
 *               </EmbeddedArrayInput>
 *         </SimpleForm>
 *     </Edit>
 * );
 * @example
 * export const CommentEdit = (props) => (
 *     <Edit {...props}>
 *         <SimpleForm>
 *              <EmbeddedArrayInput source="links">
 *                  <TextInput />
 *               </EmbeddedArrayInput>
 *         </SimpleForm>
 *     </Edit>
 * );
 */
export class EmbeddedArrayInput extends Component {
    static propTypes = {
        addLabel: PropTypes.bool.isRequired,
        addField: PropTypes.bool.isRequired,
        allowEmpty: PropTypes.bool.isRequired,
        allowAdd: PropTypes.bool.isRequired,
        allowRemove: PropTypes.bool.isRequired,
        arrayElStyle: PropTypes.object,
        basePath: PropTypes.string,
        children: PropTypes.node.isRequired,
        disabled: PropTypes.bool,
        elStyle: PropTypes.object,
        input: PropTypes.object,
        label: PropTypes.string,
        labelAdd: PropTypes.string.isRequired,
        labelRemove: PropTypes.string.isRequired,
        addText: PropTypes.string,
        meta: PropTypes.object,
        onChange: PropTypes.func,
        resource: PropTypes.string,
        readOnly: PropTypes.bool,
        record: PropTypes.object,
        source: PropTypes.string,
        customButtons: PropTypes.node,
        actionsContainerStyle: PropTypes.object,
        innerContainerStyle: PropTypes.object,
        labelContainerStyle: PropTypes.object,
        labelStyle: PropTypes.object,
        insertDividers: PropTypes.bool,
    };

    static styles = {
        item: {
            display: 'flex',
            alignItems: 'flex-start',
            marginBottom: 12,
            padding: '4px 8px 12px 16px',
            border: `1px solid ${COLORS.border}`,
            borderRadius: 12,
            backgroundColor: '#fff',
        },
        number: {
            flexShrink: 0,
            width: 26,
            height: 26,
            marginTop: 26,
            marginRight: 14,
            borderRadius: '50%',
            backgroundColor: COLORS.primaryLight,
            color: COLORS.primary,
            fontSize: 12,
            fontWeight: 700,
            lineHeight: '26px',
            textAlign: 'center',
        },
        inputs: {
            display: 'flex',
            flex: 1,
            flexWrap: 'wrap',
            minWidth: 0,
            margin: '0 -8px',
        },
        input: {
            flex: '1 1 220px',
            minWidth: 0,
            padding: '0 8px',
        },
        remove: {
            flexShrink: 0,
            marginTop: 16,
            marginLeft: 4,
            color: COLORS.muted,
        },
        empty: {
            marginBottom: 12,
            padding: '18px 16px',
            border: `1px dashed ${COLORS.border}`,
            borderRadius: 12,
            color: COLORS.muted,
            fontSize: 14,
            textAlign: 'center',
        },
    };

    static defaultProps = {
        addLabel: false,
        addField: false,
        allowEmpty: true,
        allowAdd: true,
        allowRemove: true,
        labelAdd: 'aor.input.embedded_array.add',
        labelRemove: 'aor.input.embedded_array.remove',
        insertDividers: true,
        actionsContainerStyle: {
            clear: 'both',
            margin: '1em',
            display: 'block',
            textAlign: 'right',
        },
        innerContainerStyle: {
            padding: '0 1em 1em 1em',
            width: '90%',
            display: 'inline-block',
        },
        labelContainerStyle: {
            padding: '1.2em 1em 0 0',
            width: '90%',
            display: 'inline-block',
        },
        labelStyle: {
            top: 0,
            position: 'relative',
            textTransform: 'capitalize',
        },
    };

    static contextTypes = {
        muiTheme: PropTypes.object.isRequired,
    };

    renderListItem = ({
        allowRemove,
        items,
        inputs,
        member,
        index,
        translate,
        labelRemove,
        readOnly,
        disabled,
        customButtons,
        actionsContainerStyle,
        innerContainerStyle,
    }) => {
        const removeItem = () => items.remove(index);
        const passedProps = {
            resource: this.props.resource,
            basePath: this.props.basePath,
            record: this.props.record,
        };

        const styles = EmbeddedArrayInput.styles;

        return (
            <div className="EmbeddedArrayInputItemContainer" style={styles.item}>
                <div style={styles.number}>{index + 1}</div>
                <div style={styles.inputs}>
                    {React.Children.map(
                        inputs,
                        input =>
                            input &&
                            <div
                                key={input.props.source}
                                className={`aor-input-${member}.${input.props.source}`}
                                style={{ ...styles.input, ...input.props.style }}
                            >
                                <EmbeddedArrayInputFormField input={input} prefix={member} {...passedProps} />
                            </div>,
                    )}
                </div>
                {customButtons && customButtons.map(button => React.cloneElement(button, { items, index }))}
                {allowRemove &&
                    !readOnly &&
                    !disabled &&
                    <Tooltip title="Odstrani">
                        <IconButton aria-label="Odstrani" onClick={removeItem} style={styles.remove}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>}
            </div>
        );
    };

    renderList = ({ fields: items }) => {
        const {
            children,
            style,
            translate,
            labelRemove,
            labelAdd,
            allowAdd,
            allowRemove,
            readOnly,
            disabled,
            customButtons,
            actionsContainerStyle,
            innerContainerStyle,
            addText,
        } = this.props;
        const createItem = () => items.push();
        const styles = EmbeddedArrayInput.styles;

        return (
            <div className="EmbeddedArrayInputContainer" style={{ paddingTop: 16, ...style }}>
                <div>
                    {items.map((member, index) =>
                        <div key={index}>
                            {this.renderListItem({
                                items,
                                inputs: children,
                                member,
                                index,
                                translate,
                                labelRemove,
                                allowRemove,
                                readOnly,
                                disabled,
                                customButtons,
                                actionsContainerStyle,
                                innerContainerStyle,
                            })}
                        </div>,
                    )}
                </div>
                {items.length === 0 && <div style={styles.empty}>Ni vnesenih postavk.</div>}
                {allowAdd &&
                    !readOnly &&
                    !disabled &&
                    <Button variant="outlined" color="primary" onClick={createItem}>
                        <AddIcon style={{ marginRight: 6 }} />
                        {addText || 'Dodaj'}
                    </Button>}
            </div>
        );
    };

    render() {
        const { source, label, addLabel, translate, resource, labelStyle, labelContainerStyle } = this.props;
        const labelStyleMuiTheme = Object.assign(labelStyle, {
            color: this.context.muiTheme ? this.context.muiTheme.textField.focusColor : '',
        });

        const minimizedLabel =
            typeof label !== 'undefined'
                ? translate(label, { _: label })
                : translate(
                      `resources.${resource}.fields.${source.replace(/\./g, '.fields.').replace(/\[\d+\]/g, '')}.name`,
                      {
                          _: inflection.humanize(source.split('.').pop()),
                      },
                  );

        const labelElement =
            !addLabel &&
            <div style={labelContainerStyle}>
                {minimizedLabel}
            </div>;

        return (
            <div>
                <FieldArray name={source} component={this.renderList} props={this.props} label={labelElement} />
            </div>
        );
    }
}

export default translate(EmbeddedArrayInput);
